import React, { useState } from "react";
import DataTable from "../../../components/DataTable";
import DuplicateModal, {
  DuplicateFormValues,
} from "../../../components/DuplicateModal";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { LessonEntity } from "../../../services/lessons/lessons.types";
import { Alert, Button, Tooltip, App } from "antd";
import { apiSlice } from "../../../store/api/apiSlice";
import {
  useGetLessonsPaginatedQuery,
  useUpdateLessonTitleMutation,
  useDeleteLessonMutation,
  useDuplicateLessonMutation,
} from "../../../store/api/lessonsApi";
import InlineTextEdit from "../../../components/InlineTextEdit";
import TableSearchBar from "../../../components/TableSearchBar";
import { useTableUrlState } from "../../../hooks/useTableUrlState";
import { useClampPageToTotal } from "../../../hooks/useClampPageToTotal";
import { showDeleteUndoNotification } from "../../../utils/delete-undo";
import PageHeader from "../../../components/PageHeader";
import LinkButton from "../../../components/LinkButton";
import ActivityIndicator from "../../../components/ActivityIndicator";
import { useIsInitialLoad } from "../../../hooks/useIsInitialLoad";

// URL param name → commit mode; the URL is the source of truth for page,
// pageSize and filters (deep-linkable, survives refreshes and back/forward).
const LESSONS_FILTERS = {
  id: "debounced",
  title: "debounced",
  course: "debounced",
} as const;

const Lessons: React.FC = () => {
  const { notification } = App.useApp();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { page, pageSize, filters, inputs, setFilter, onPageChange } =
    useTableUrlState({ filters: LESSONS_FILTERS });

  // `data` (instead of `currentData`) keeps the previous page visible while
  // the next one is fetching, so the table doesn't flicker to empty.
  const { data, isFetching, isError, refetch } = useGetLessonsPaginatedQuery({
    page,
    pageSize,
    lessonId: /^\d+$/.test(filters.id) ? parseInt(filters.id, 10) : undefined,
    lessonTitle: filters.title || undefined,
    courseTitle: filters.course || undefined,
  });
  const [deleteLesson] = useDeleteLessonMutation();
  const [updateLessonTitle] = useUpdateLessonTitleMutation();
  const [duplicateLesson, { isLoading: isDuplicating }] =
    useDuplicateLessonMutation();
  const [lessonToDuplicate, setLessonToDuplicate] =
    useState<LessonEntity | null>(null);

  const lessons = data?.data ?? [];
  const pagination = data?.pagination ?? { page, pageSize, total: 0 };
  const isInitialLoad = useIsInitialLoad(isFetching, lessons);

  // Deleting the last item of the last page must not leave an empty table.
  useClampPageToTotal({
    total: pagination.total,
    pageSize,
    page,
    onPageChange: (lastPage) => onPageChange(lastPage, pageSize),
  });

  const handleEdit = (record: LessonEntity) => {
    navigate(`/lessons/${record.id}`, { state: record.id });
  };

  const handleDelete = async (record: LessonEntity) => {
    try {
      await deleteLesson(record.id).unwrap();
      showDeleteUndoNotification({
        entity: "lesson",
        id: record.id,
        title: record.title,
        // The delete mutation already invalidates the list; restoring from
        // the trash happens outside RTK Query, so both the entity list and
        // the trash cache must be invalidated by hand.
        onRestored: () => {
          dispatch(
            apiSlice.util.invalidateTags([
              { type: "Lesson", id: "LIST" },
              "Trash",
            ]),
          );
        },
      });
    } catch (error: any) {
      notification["error"]({
        message: "Eliminación Erronea",
        description:
          error?.data?.message || "La lección no pudo ser eliminada!",
        placement: "top",
        duration: 3,
      });
    }
  };

  const handleDuplicateConfirm = async ({
    title,
    children_mode,
  }: DuplicateFormValues) => {
    if (!lessonToDuplicate) return;
    try {
      const copy = await duplicateLesson({
        id: lessonToDuplicate.id,
        title,
        children_mode,
      }).unwrap();
      setLessonToDuplicate(null);
      notification["success"]({
        message: "Lección duplicada",
        description:
          children_mode === "link"
            ? `Se creó "${copy.title}" compartiendo las actividades del original (sin vincular a ningún curso).`
            : `Se creó "${copy.title}" como copia independiente (sin vincular a ningún curso).`,
        placement: "top",
        duration: 5,
        btn: (
          <Button type="primary" onClick={() => navigate(`/lessons/${copy.id}`)}>
            Editar copia
          </Button>
        ),
      });
    } catch (error: any) {
      notification["error"]({
        message: "Duplicación errónea",
        description:
          error?.data?.message || "La lección no pudo ser duplicada.",
        placement: "top",
        duration: 3,
      });
    }
  };

  return (
    <div>
      <PageHeader
        title="Lecciones"
        extra={
          <TableSearchBar
            fields={[
              {
                placeholder: "Buscar por ID",
                value: inputs.id,
                onChange: (value) => setFilter("id", value),
                width: 200,
              },
              {
                placeholder: "Buscar por título",
                value: inputs.title,
                onChange: (value) => setFilter("title", value),
                width: 200,
              },
              {
                placeholder: "Buscar por curso",
                value: inputs.course,
                onChange: (value) => setFilter("course", value),
                width: 200,
              },
            ]}
          >
            <LinkButton type="primary" to="/lessons/new">
              Nueva lección
            </LinkButton>
          </TableSearchBar>
        }
      />
      {isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="No se pudieron cargar las lecciones."
          action={
            <Button size="small" onClick={() => refetch()}>
              Reintentar
            </Button>
          }
        />
      )}
      {isInitialLoad ? (
        <ActivityIndicator />
      ) : (
        <DataTable
          data={lessons}
          columns={[
            { title: "ID", dataIndex: "id", key: "id", width: 80 },
            {
              title: "Título",
              dataIndex: "title",
              key: "title",
              width: 300,
              // Inline rename: a safe partial PATCH (only the title travels),
              // so a small fix never requires opening the full form.
              render: (title: string, record: LessonEntity) => (
                <InlineTextEdit
                  value={title}
                  editLabel={`Editar título de ${title}`}
                  successMessage="Título de la lección actualizado."
                  onSave={(next) =>
                    updateLessonTitle({ id: record.id, title: next }).unwrap()
                  }
                />
              ),
            },
            {
              title: "Grado",
              dataIndex: "education_level",
              key: "education_level",
              width: 120,
              render: (level: string) => {
                if (level === "KINDER") return "Preescolar";
                if (level === "PRIMARIA") return "Primaria";
                return "-";
              },
            },
            {
              title: "Programas",
              dataIndex: "programs",
              key: "programs",
              width: 200,
              // Tras una duplicación varias lecciones comparten título; el
              // programa al que pertenece cada copia es lo que las distingue.
              render: (
                programs: Array<{
                  id: number;
                  title: string;
                  program_code: string | null;
                }>,
              ) => {
                if (!programs || programs.length === 0) {
                  return "NO ASIGNADO";
                }
                const programsText = programs
                  .map((program) => program.program_code ?? program.title)
                  .join(", ");
                return (
                  <Tooltip title={programsText} placement="topLeft">
                    <div
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <strong>{programsText}</strong>
                    </div>
                  </Tooltip>
                );
              },
            },
            {
              title: "Cursos asociados",
              dataIndex: "courses",
              key: "courses",
              width: 400,
              render: (courses: Array<{ id: number; title: string }>) => {
                if (!courses || courses.length === 0) {
                  return "NO ASIGNADO";
                }
                const coursesText = courses
                  .map((course) => course.title)
                  .join(", ");
                return (
                  <Tooltip title={coursesText} placement="topLeft">
                    <div
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {coursesText}
                    </div>
                  </Tooltip>
                );
              },
            },
            {
              title: "Proyectos asociados",
              dataIndex: "projects",
              key: "projects",
              width: 300,
              render: (projects: Array<{ id: number; title: string }>) => {
                if (!projects || projects.length === 0) {
                  return "NO ASIGNADO";
                }
                const projectsText = projects
                  .map((project) => project.title)
                  .join(", ");
                return (
                  <Tooltip title={projectsText} placement="topLeft">
                    <div
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {projectsText}
                    </div>
                  </Tooltip>
                );
              },
            },
          ]}
          onEdit={handleEdit}
          getEditHref={(record) => `/lessons/${record.id}`}
          onDuplicate={(record) => setLessonToDuplicate(record)}
          onDelete={handleDelete}
          loading={isFetching}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: onPageChange,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "30", "50"],
          }}
        />
      )}
      <DuplicateModal
        open={lessonToDuplicate !== null}
        entityLabel="lección"
        originalTitle={lessonToDuplicate?.title ?? ""}
        childrenLabel="las actividades de la lección"
        loading={isDuplicating}
        onCancel={() => setLessonToDuplicate(null)}
        onConfirm={handleDuplicateConfirm}
      />
    </div>
  );
};

export default Lessons;
